/**
 * Tugma — ilovadagi eng ko'p bosiladigan element.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ RANG QIYMATI EMAS, RANG ARALASHMASI                                     ║
 * ║                                                                          ║
 * ║ Ilgari hover `opacity-90` edi. U ikki joyda yolg'on chiqardi:            ║
 * ║   • shaffoflik tugma ORQASIDAGI fonni ko'rsatadi — jadval satri ustidagi ║
 * ║     tugma boshqacha, karta ustidagisi boshqacha rangga kirardi;          ║
 * ║   • qorong'i mavzuda «xiralashish» tugmani fonga singdirib yuborardi,    ║
 * ║     ya'ni sichqoncha ustiga kelganda u KUCHSIZLANARDI — kutilganning     ║
 * ║     aksi.                                                                ║
 * ║                                                                          ║
 * ║ Endi hover rangni `--color-ink` bilan aralashtiradi. `ink` yorug'        ║
 * ║ mavzuda deyarli qora, qorong'ida deyarli oq — demak BITTA qoida yorug'da ║
 * ║ to'qlashtiradi, qorong'ida yoritadi. Ikkita alohida `dark:` varianti     ║
 * ║ yozish shart emas va mavzu tokeni o'zgarsa tugma o'zi moslashadi.        ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { SpinnerGlyph } from './spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerOutline';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /**
   * So'rov ketayotgani. Tugma bloklanadi va ichida spinner aylanadi.
   *
   * Matn OLIB TASHLANMAYDI — faqat ko'rinmas qilinadi (pastga qarang):
   * shuning uchun tugma eni bir pikselga ham o'zgarmaydi.
   */
  readonly loading?: boolean;
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ Har bir variant uchun uch holat: tinch, hover, BOSILGAN.                 │
 * │                                                                          │
 * │ Bosilgan holat ilgari umuman yo'q edi. Sekin tarmoqda foydalanuvchi      │
 * │ tugma bosilganini sezmay, uni ikkinchi marta bosardi — hujjatlar         │
 * │ ikkilanishining eng arzon sababi. `active:` rangni yana bir pog'ona      │
 * │ to'qlashtiradi va tugma 1px pastga tushadi: bu «qabul qilindi» degan     │
 * │ darhol javob, tarmoqni kutmaydi.                                        │
 * │                                                                          │
 * │ `enabled:` prefiksi ATAYLAB: usiz o'chirilgan tugma ham hover'da rang    │
 * │ o'zgartirar va bosilsa qimirlar edi — ya'ni bosilmaydigan tugma          │
 * │ bosilayotgandek ko'rinardi.                                             │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-brand text-brand-ink border-transparent shadow-sm',
    'enabled:hover:bg-[color-mix(in_oklab,var(--color-brand)_88%,var(--color-ink))]',
    'enabled:active:bg-[color-mix(in_oklab,var(--color-brand)_76%,var(--color-ink))]',
  ),
  secondary: cn(
    'bg-surface text-ink border-line',
    'enabled:hover:bg-surface-2 enabled:hover:border-[color-mix(in_oklab,var(--color-brand)_35%,var(--color-line))]',
    'enabled:active:bg-[color-mix(in_oklab,var(--color-surface-2)_88%,var(--color-ink))]',
  ),
  ghost: cn(
    'bg-transparent text-ink-muted border-transparent',
    'enabled:hover:bg-surface-2 enabled:hover:text-ink',
    'enabled:active:bg-[color-mix(in_oklab,var(--color-surface-2)_88%,var(--color-ink))]',
  ),
  danger: cn(
    'bg-danger text-white border-transparent shadow-sm',
    'enabled:hover:bg-[color-mix(in_oklab,var(--color-danger)_88%,var(--color-ink))]',
    'enabled:active:bg-[color-mix(in_oklab,var(--color-danger)_76%,var(--color-ink))]',
  ),
  /*
   * ╔══════════════════════════════════════════════════════════════════════════╗
   * ║ `dangerOutline` — TAKRORLANADIGAN xavfli amal uchun                     ║
   * ║                                                                          ║
   * ║ `danger` to'ldirilgan va u dizayn tizimidagi ENG baland ovoz. Jadvalning ║
   * ║ har satrida takrorlanganda ikki narsa buziladi: qizil rang «xavf»        ║
   * ║ ma'nosini yo'qotadi (u ekranning asosiy rangiga aylanadi) va ma'lumot    ║
   * ║ tugmalar ostida qolib ketadi. Ustalar ro'yxatida aynan shunday edi —     ║
   * ║ sakkizta to'ldirilgan qizil «Bloklash» ustma-ust turardi.                ║
   * ║                                                                          ║
   * ║ Endi satrdagi tugma KONTURLI, to'ldirilgani esa oynadagi TASDIQLASH      ║
   * ║ tugmasiga qoldiriladi — ya'ni qizil fon bir joyda va bir marta.          ║
   * ║                                                                          ║
   * ║ ⚠️ NIMA UCHUN ALOHIDA VARIANT, `ghost` USTIGA `className` EMAS           ║
   * ║                                                                          ║
   * ║ Aynan shunday qilib ko'rildi va u JIMGINA ISHLAMADI. Sabab —             ║
   * ║ `lib/cn.ts`: u sinflarni shunchaki BIRLASHTIRADI, `tailwind-merge`       ║
   * ║ emas. Ya'ni `ghost` dagi `text-ink-muted` va qo'shilgan `text-danger`    ║
   * ║ ikkalasi ham chiqadi va g'olibni sinf atributidagi tartib emas,          ║
   * ║ CSS FAYLIDAGI tartib hal qiladi. Jonli tekshirildi: to'plangan           ║
   * ║ faylda `.text-ink-muted` `.text-danger` dan KEYIN turadi, ya'ni tugma    ║
   * ║ kulrang chiqardi — eng xavfli amal hech qanday ogohlantirish rangisiz.   ║
   * ║                                                                          ║
   * ║ Bunday to'qnashuvni sinf qo'shib «yengib» bo'lmaydi va uni ko'rib ham    ║
   * ║ bo'lmaydi — shuning uchun yechim variantning O'ZIDA.                     ║
   * ╚══════════════════════════════════════════════════════════════════════════╝
   */
  dangerOutline: cn(
    'bg-transparent text-danger border-danger/50',
    'enabled:hover:border-danger enabled:hover:bg-danger-soft',
    'enabled:active:bg-[color-mix(in_oklab,var(--color-danger-soft)_88%,var(--color-danger))]',
  ),
};

/*
 * O'lchamlar. `sm` = 36px — bu bezak emas, eng kichik ISHONCHLI nishon:
 * WCAG 2.2 (§2.5.8) 24px ni minimum deydi, sensorli ekranda esa 36px dan
 * pastida omborchi barmog'i yonidagi tugmani bosib yuboradi. Ilgari `sm`
 * 32px edi va jadval satridagi «tahrirlash / o'chirish» juftligi aynan
 * shuning uchun xato bosilardi.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
};

/*
 * Belgi va yozuv orasidagi masofa TUGMADA emas, ichki qatlamda beriladi —
 * spinner tugmaning o'zida absolyut joylashgani uchun tashqi `gap` unga
 * ta'sir qilmasdi va faqat chalg'itardi.
 */
const GAPS: Record<ButtonSize, string> = {
  sm: 'gap-1.5',
  md: 'gap-2',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
  type = 'button',
  disabled,
  children,
  ...props
}: ButtonProps): ReactNode {
  return (
    <button
      // `type` default 'submit' — form ichida tasodifan yuborilishning eng ko'p sababi.
      type={type}
      // Yuklanayotgan tugma bosilmasligi kerak: ikki marta bosish = ikki hujjat.
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex items-center justify-center rounded-md border font-medium',
        'transition-[background-color,border-color,box-shadow,transform] duration-150',
        // Harakat sozlamasi hurmat qilinadi — bosilgandagi 1px siljish o'chadi.
        'motion-safe:enabled:active:translate-y-px',
        /*
         * Fokus halqasi. `styles.css` da global `:focus-visible` bor, lekin
         * u 1px offset bilan — to'ldirilgan tugmada halqa tugmaning o'z
         * rangiga tegib, ko'rinmay qolardi. Bu yerda 2px offset beriladi:
         * halqa bilan tugma orasida fon rangi qoladi va u har qanday
         * variantda ajralib turadi.
         */
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        /*
         * O'CHIRILGAN HOLAT — ko'rinadi, lekin bosilmasligi bilinadi.
         *
         * `opacity-50` RAD ETILDI: matn fonga singib, ruxsati yo'q
         * foydalanuvchi tugmada nima yozilganini umuman o'qiy olmasdi va
         * «nega bosilmayapti» degan savolga javob yo'q edi. 60% + rang
         * to'yinganligini pasaytirish — yozuv o'qiladi, ammo tugma
         * «o'lik» ko'rinadi.
         */
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:saturate-50 disabled:shadow-none',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...props}
    >
      {/*
       * Yuklanishda matn O'CHIRILMAYDI, `invisible` qilinadi.
       *
       * Matnni spinnerga almashtirish birinchi o'ylangan yo'l edi va u
       * tugma enini «Buyurtmani tasdiqlash» dan bitta doiragacha
       * qisqartirardi: yonidagi tugmalar sakrab, sichqoncha ostidagi
       * tugma boshqasiga aylanardi. Endi joy o'sha joyda qoladi.
       */}
      <span className={cn('inline-flex items-center', GAPS[size], loading && 'invisible')}>{children}</span>
      {loading && (
        <span className="absolute inset-0 inline-flex items-center justify-center">
          <SpinnerGlyph />
        </span>
      )}
    </button>
  );
}
